import type {
  Aircraft,
  AircraftDocument,
  AircraftDocumentType,
  HoursCounters,
} from '../types';
import { api } from './http';

export const createAircraft = (data: {
  tail_number: string;
  model?: string;
  purchase_date: string;
  condition: 'new' | 'used';
  airframe_hours?: number;
  engine_hours?: number;
  tach_at_purchase?: number;
  base_airfield?: string | null;
  maintenance_type?: 'technician' | 'organization' | 'self' | null;
  maintenance_name?: string | null;
}) => api.post<Aircraft & { created?: boolean }>('/aircraft', data);

export const deleteAircraft = (aircraftId: number) =>
  api.delete<{ id: number; tail_number: string; deleted: boolean }>(
    `/aircraft/${aircraftId}`
  );
export const getHoursCounters = () =>
  api.get<HoursCounters>('/aircraft/hours');
export const getAircraft = () => api.get<Aircraft>('/aircraft');
export const getAircraftList = () =>
  api.get<Aircraft[]>('/aircraft/list').then((res) => res.data);
export const updateAircraftFuel = (
  data: {
    tail_number?: string;
    model?: string;
    avg_fuel_burn_lph?: number | null;
    fuel_price_per_liter?: number | null;
    base_airfield?: string | null;
    maintenance_type?: 'technician' | 'organization' | 'self' | null;
    maintenance_name?: string | null;
    purchase_date?: string;
    condition?: 'new' | 'used';
    airframe_hours?: number;
    engine_hours?: number;
    tach_at_purchase?: number;
  },
  aircraftId?: number
) =>
  api.put<Aircraft>('/aircraft/fuel', data, {
    params: aircraftId && aircraftId > 0 ? { aircraft_id: aircraftId } : undefined,
  });
export const updateAircraftSlg = (data: { slg_date: string | null }) =>
  api.put<{
    updated: boolean;
    aircraft_id: number;
    tail_number: string;
    slg: Aircraft['slg'];
  }>('/aircraft/slg', data);
export const updateAircraftInsurance = (data: {
  insurance_date: string | null;
}) =>
  api.put<{
    updated: boolean;
    aircraft_id: number;
    tail_number: string;
    insurance: Aircraft['insurance'];
  }>('/aircraft/insurance', data);
export const updateAircraftRegistration = (data: {
  registration_date: string | null;
}) =>
  api.put<{
    updated: boolean;
    aircraft_id: number;
    tail_number: string;
    registration: Aircraft['registration'];
  }>('/aircraft/registration', data);
export const getAircraftDocuments = () =>
  api.get<AircraftDocument[]>('/aircraft/documents');
export const getAircraftDocumentFile = (documentId: number) =>
  api.get<Blob>(`/aircraft/documents/${documentId}/file`, {
    responseType: 'blob',
  });
export const uploadAircraftDocument = (
  file: File,
  doc_type: AircraftDocumentType = 'other',
  group_id?: string
) => {
  const form = new FormData();
  form.append('file', file);
  form.append('doc_type', doc_type);
  if (group_id) {
    form.append('group_id', group_id);
  }
  return api.post<AircraftDocument>('/aircraft/documents', form, {
    transformRequest: [
      (data, headers) => {
        if (data instanceof FormData) {
          delete headers['Content-Type'];
        }
        return data;
      },
    ],
  });
};
export const downloadAircraftDocument = async (
  documentId: number,
  filename: string
) => {
  const res = await getAircraftDocumentFile(documentId);
  const url = window.URL.createObjectURL(res.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
export const deleteAircraftDocument = (documentId: number) =>
  api.delete<{ deleted: boolean; id: number }>(
    `/aircraft/documents/${documentId}`
  );
